import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Globe, Zap, ChevronDown } from "lucide-react";
import { Button } from "../ui/button";
import { useLanguage } from "../../i18n/LanguageContext";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const location = useLocation();
  const { t, lang, setLang } = useLanguage();

  const navLinks = [
    { label: t("nav.services"), href: "#services" },
    { label: t("nav.whyUs"), href: "#why-us" },
    { label: t("nav.caseStudies"), href: "#case-studies" },
    { label: t("nav.contact"), href: "#contact" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!e.target.closest("[data-lang-menu]")) setLangOpen(false);
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, []);

  const handleAnchor = (href) => {
    setOpen(false);
    if (location.pathname !== "/") {
      window.location.href = "/" + href;
      return;
    }
    const id = href.replace("#", "");
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const chooseLang = (code) => {
    setLang(code);
    setLangOpen(false);
  };

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "backdrop-blur-xl bg-[#050818]/80 border-b border-cyan-400/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group">
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-br from-sky-500 to-cyan-400 blur-md opacity-60 group-hover:opacity-90 transition" />
            <div className="relative w-11 h-11 rounded-xl bg-gradient-to-br from-sky-500 to-cyan-400 flex items-center justify-center shadow-lg shadow-cyan-500/20">
              <Zap className="w-6 h-6 text-white" strokeWidth={2.5} />
            </div>
          </div>
          <div className="leading-tight">
            <div className="font-display font-bold text-white text-lg">FortressPoint</div>
            <div className="text-[11px] tracking-widest uppercase text-cyan-300/80">Technology Solutions</div>
          </div>
        </Link>

        <div className="hidden lg:flex items-center gap-2">
          <div className="hidden xl:flex items-center gap-1 px-3 py-1.5 rounded-full border border-cyan-400/20 bg-cyan-500/5 mr-4">
            <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
            <span className="text-[11px] tracking-wider text-cyan-200/80">{t("nav.kicker")}</span>
          </div>
          {navLinks.map((l) => (
            <button
              key={l.href}
              onClick={() => handleAnchor(l.href)}
              className="px-4 py-2 text-sm text-slate-200 hover:text-white rounded-lg hover:bg-white/5 transition"
            >
              {l.label}
            </button>
          ))}

          {/* Language toggle */}
          <div className="relative" data-lang-menu>
            <button
              onClick={(e) => { e.stopPropagation(); setLangOpen((s) => !s); }}
              className="px-3 py-2 text-sm text-slate-200 hover:text-white rounded-lg hover:bg-white/5 transition flex items-center gap-1.5"
              aria-label="Toggle language"
            >
              <Globe className="w-4 h-4" /> {lang === "fr" ? "FR" : "EN"} <ChevronDown className={`w-3.5 h-3.5 transition ${langOpen ? "rotate-180" : ""}`} />
            </button>
            {langOpen && (
              <div className="absolute right-0 mt-2 w-56 rounded-xl border border-cyan-400/15 bg-[#0a1030]/95 backdrop-blur-xl shadow-xl shadow-cyan-500/10 overflow-hidden">
                <LangItem active={lang === "en"} label="English" sub="United States" flag="EN" onClick={() => chooseLang("en")} />
                <LangItem active={lang === "fr"} label="Français" sub="Canada" flag="FR" onClick={() => chooseLang("fr")} />
              </div>
            )}
          </div>

          <Button onClick={() => handleAnchor("#contact")} className="btn-primary rounded-lg px-5 h-10 font-semibold text-sm border-0">
            {t("nav.getStarted")}
          </Button>
        </div>

        <button className="lg:hidden text-white p-2" onClick={() => setOpen((s) => !s)} aria-label="menu">
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-[#050818]/95 border-t border-cyan-400/10 backdrop-blur-xl">
          <div className="px-6 py-4 flex flex-col gap-2">
            {navLinks.map((l) => (
              <button key={l.href} onClick={() => handleAnchor(l.href)} className="text-left px-3 py-2.5 rounded-lg text-slate-200 hover:bg-white/5">
                {l.label}
              </button>
            ))}
            <div className="flex items-center gap-2 px-3 py-2">
              <span className="text-[11px] uppercase tracking-widest text-slate-500">Language</span>
              <button onClick={() => chooseLang("en")} className={`px-3 py-1 rounded-lg text-xs font-semibold border ${lang === "en" ? "border-cyan-400/50 text-cyan-200 bg-cyan-500/10" : "border-white/10 text-slate-300"}`}>EN</button>
              <button onClick={() => chooseLang("fr")} className={`px-3 py-1 rounded-lg text-xs font-semibold border ${lang === "fr" ? "border-cyan-400/50 text-cyan-200 bg-cyan-500/10" : "border-white/10 text-slate-300"}`}>FR</button>
            </div>
            <Button onClick={() => handleAnchor("#contact")} className="btn-primary rounded-lg h-11 font-semibold border-0 mt-2">
              {t("nav.getStarted")}
            </Button>
          </div>
        </div>
      )}
    </header>
  );
}

function LangItem({ active, label, sub, flag, onClick }) {
  return (
    <button onClick={onClick} className={`w-full flex items-center justify-between px-4 py-3 text-left transition ${active ? "bg-cyan-500/10" : "hover:bg-white/5"}`}>
      <div>
        <div className="text-sm font-semibold text-white">{label}</div>
        <div className="text-xs text-slate-400">{sub}</div>
      </div>
      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold tracking-widest ${active ? "bg-cyan-500/25 text-cyan-100 border border-cyan-400/40" : "bg-white/5 text-slate-400 border border-white/10"}`}>{flag}</span>
    </button>
  );
}
